import * as React from 'react'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import CloseIcon from '@mui/icons-material/Close'

import { useSnackbar } from '../Snackbar'
import {
    downloadVisibleStatementsCsv,
    downloadVisibleStatementsMarkdown,
} from './visibleStatementExports'
import { downloadGraphJson } from './graphJsonExport'
import { downloadGraphPng } from '../LeftMenuMigration/canvasSnapshot'

const showSnackbarSelector = state => state.showSnackbar

/**
 * Trigger a download by programmatically clicking the legacy `<a>`
 * element with the given id. The legacy click handler sets `href` and
 * the `download` attribute synchronously before the browser follows the
 * link, so the file the user gets is prepared without our involvement.
 *
 * Returns false (with a console warn) if the element is missing — caller
 * surfaces a user-facing error.
 */
function triggerLegacyAnchor(legacyId) {
    const el = document.getElementById(legacyId)
    if (!el) {
        console.warn(`[DownloadDialog] missing legacy element #${legacyId}`)
        return false
    }
    // Synthetic click runs the bound handler then follows the href.
    el.click()
    return true
}

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>
        {children}
    </Typography>
)

const SectionHelp = ({ children }) => (
    <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: 'block' }}
    >
        {children}
    </Typography>
)

const DownloadButton = ({ label, onClick, ...rest }) => (
    <Button
        variant="outlined"
        size="small"
        onClick={onClick}
        sx={{ textTransform: 'none' }}
        {...rest}
    >
        {label}
    </Button>
)

/**
 * Download / Export dialog opened from the floating tools bar.
 *
 * Sections:
 *   - Network Graph Image (PNG) — native React canvas snapshot
 *   - Visible Statements (CSV, MD) — native React, generates inline
 *   - Text Mining Analytics (TXT, Keywords, CSV report, N-Grams)
 *     — delegates to legacy `<a>` tags
 *   - Graph Data — JSON is native React (fetch + context_statements_map
 *     transform, see graphJsonExport.js); CSV, Gexf delegate to legacy `<a>` tags
 *   - Original Text (TXT, JSON) — delegates to legacy `<a>` tags
 *
 * The delegated paths require the legacy `#downloadgraphdata` panel to
 * stay in the DOM (it's hidden but present). HideLegacyDom only hides
 * `#statementmenu`.
 */
export default function DownloadDialog({ open, onClose }) {
    const showSnackbar = useSnackbar(showSnackbarSelector)

    const onLegacy = legacyId => () => {
        const ok = triggerLegacyAnchor(legacyId)
        if (!ok) {
            showSnackbar(
                'error',
                'Download unavailable. Please reload the page and try again.'
            )
        }
    }

    const onPng = () => {
        const ok = downloadGraphPng()
        if (!ok) {
            showSnackbar(
                'error',
                'Could not capture the graph image — graph canvas not ready.'
            )
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ pr: 6 }}>
                Export Data
                <IconButton
                    aria-label="close-download-dialog"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme => theme.palette.text.secondary,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <SectionTitle>Network Graph Image</SectionTitle>
                <SectionHelp>
                    For publishing on the web or in a journal. For embeds and
                    URLs use the share menu.
                </SectionHelp>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <DownloadButton
                        label="PNG (Image)"
                        onClick={onPng}
                        data-testid="dl-png"
                    />
                </Stack>

                <Divider sx={{ my: 2 }} />
                <SectionTitle>Visible Statements</SectionTitle>
                <SectionHelp>
                    Export currently filtered (visible) statements with all
                    meta-data tags (topics, sentiment, hashtags).
                </SectionHelp>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <DownloadButton
                        label="CSV (Spreadsheet)"
                        onClick={() => {
                            const result = downloadVisibleStatementsCsv()
                            if (!result.ok) showSnackbar('error', result.error)
                        }}
                        data-testid="dl-statements-csv"
                    />
                    <DownloadButton
                        label="MD (e.g. Obsidian)"
                        onClick={() => {
                            const result = downloadVisibleStatementsMarkdown()
                            if (!result.ok) showSnackbar('error', result.error)
                        }}
                        data-testid="dl-statements-md"
                    />
                </Stack>

                <Divider sx={{ my: 2 }} />
                <SectionTitle>Text Mining Analytics</SectionTitle>
                <SectionHelp>
                    Summary of insights from the analytics panel.
                </SectionHelp>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <DownloadButton
                        label="TXT Analytics Report"
                        onClick={onLegacy('downloadReportFromExport')}
                        data-testid="dl-report-txt"
                    />
                    <DownloadButton
                        label="Keywords"
                        onClick={onLegacy('downloadKeywordsFromStatementMenu')}
                        data-testid="dl-keywords"
                    />
                    <DownloadButton
                        label="CSV Report"
                        onClick={onLegacy('downloadCSVReportFromExport')}
                        data-testid="dl-report-csv"
                    />
                    <DownloadButton
                        label="N-Grams CSV"
                        onClick={onLegacy('downloadNGrams')}
                        data-testid="dl-ngrams"
                    />
                </Stack>

                <Divider sx={{ my: 2 }} />
                <SectionTitle>Network Graph Data</SectionTitle>
                <SectionHelp>
                    Raw data with all statistics for further analysis in
                    another software.
                </SectionHelp>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <DownloadButton
                        label="JSON"
                        onClick={async () => {
                            const result = await downloadGraphJson()
                            if (!result.ok) showSnackbar('error', result.error)
                        }}
                        data-testid="dl-graph-json"
                    />
                    <DownloadButton
                        label="CSV"
                        onClick={onLegacy('download-link-graph-csv')}
                        data-testid="dl-graph-csv"
                    />
                    <DownloadButton
                        label="Gexf (Gephi)"
                        onClick={onLegacy('exportgexf')}
                        data-testid="dl-gexf"
                    />
                </Stack>

                <Divider sx={{ my: 2 }} />
                <SectionTitle>Original Text</SectionTitle>
                <SectionHelp>For backup and duplicating.</SectionHelp>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <DownloadButton
                        label="TXT (no meta-data)"
                        onClick={onLegacy('download-link-text')}
                        data-testid="dl-text-txt"
                    />
                    <DownloadButton
                        label="JSON (with tags)"
                        onClick={onLegacy('download-link-text-json')}
                        data-testid="dl-text-json"
                    />
                </Stack>
            </DialogContent>
        </Dialog>
    )
}
